import styled from "styled-components";
import React, { useState } from "react";
import { FiChevronDown, FiChevronUp, FiMapPin } from "react-icons/fi";
import { MdClose } from "react-icons/md";

const PropertyFilterContainer = styled.div`
  background: #ffffff;
  border-radius: 20px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
  padding: 0;
  max-height: ${(props) => {
    const itemCount = props.itemCount || 0;
    if (itemCount <= 3) return "400px";
    if (itemCount <= 5) return "550px";
    if (itemCount <= 8) return "700px";
    return "850px";
  }};
  min-height: 300px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    box-shadow: 0 12px 45px rgba(0, 0, 0, 0.12);
  }

  @media (max-width: 768px) {
    padding: 0;
    max-height: none;
    min-height: auto;
  }
`;

const FilterHeader = styled.div`
  padding: 24px;
  background: linear-gradient(135deg, #c29957 0%, #a67c42 100%);
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-radius: 20px 20px 0 0;

  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const FilterTitle = styled.h3`
  font-family: "Playfair Display", Georgia, serif;
  font-size: 20px;
  font-weight: 700;
  color: #ffffff;
  margin: 0;
  letter-spacing: -0.3px;
  display: flex;
  align-items: center;
  gap: 10px;

  svg {
    width: 20px;
    height: 20px;
  }

  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const ResetButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  color: #ffffff;
  border: 1.5px solid rgba(255, 255, 255, 0.3);
  padding: 8px 18px;
  border-radius: 25px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  gap: 6px;
  letter-spacing: 0.3px;
  text-transform: uppercase;

  svg {
    width: 14px;
    height: 14px;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    border-color: rgba(255, 255, 255, 0.5);
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.98);
  }
`;

const FilterContent = styled.div`
  padding: 20px;
  overflow-y: auto;
  flex: 1;

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f8f6f3;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c29957;
    border-radius: 10px;
    transition: background 0.3s;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #a67c42;
  }

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #6b6b6b;
  font-size: 15px;
  font-weight: 500;

  svg {
    width: 48px;
    height: 48px;
    color: #c29957;
    margin-bottom: 12px;
    opacity: 0.5;
  }
`;

const TownFilter = styled.div`
  margin-bottom: 12px;
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.3s ease;

  &:last-child {
    margin-bottom: 0;
  }
`;

const TownHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  padding: 16px 18px;
  background: linear-gradient(135deg, #f8f6f3 0%, #f0ede8 100%);
  border-radius: 12px;
  font-weight: 700;
  color: #1a1a1a;
  font-size: 16px;
  border: 2px solid transparent;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background: #c29957;
    border-radius: 12px 0 0 12px;
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    background: linear-gradient(135deg, #fff9f0 0%, #f8f6f3 100%);
    border-color: rgba(194, 153, 87, 0.2);
    transform: translateX(4px);

    &::before {
      opacity: 1;
    }
  }

  &:active {
    transform: translateX(2px) scale(0.99);
  }

  @media (max-width: 768px) {
    font-size: 15px;
    padding: 14px 16px;
  }

  span:first-child {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  svg {
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    color: #c29957;
  }

  &:hover svg {
    transform: scale(1.1);
  }
`;

const TownOptions = styled.ul`
  padding: 8px 0 0 0;
  list-style-type: none;
  margin: 0;
  animation: slideDown 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const TownOption = styled.li`
  padding: 12px 18px;
  margin: 4px 0;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  color: #1a1a1a;
  background: #ffffff;
  border: 1.5px solid #e8e8e8;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 3px;
    background: #c29957;
    transform: scaleY(0);
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  &:hover {
    background: linear-gradient(135deg, #fff9f0 0%, #ffffff 100%);
    border-color: #c29957;
    transform: translateX(8px);
    box-shadow: 0 4px 12px rgba(194, 153, 87, 0.15);

    &::before {
      transform: scaleY(1);
    }
  }

  &:active {
    transform: translateX(6px) scale(0.98);
  }

  @media (max-width: 768px) {
    font-size: 13px;
    padding: 10px 16px;
  }
`;

const PropertyCount = styled.span`
  background: linear-gradient(135deg, #c29957 0%, #a67c42 100%);
  color: #ffffff;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.3px;
  box-shadow: 0 2px 6px rgba(194, 153, 87, 0.25);
  transition: all 0.3s ease;

  ${TownOption}:hover & {
    transform: scale(1.05);
  }
`;

const ChevronIcon = styled.span`
  display: flex;
  align-items: center;
  color: #c29957;
  font-size: 18px;

  svg {
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  ${TownHeader}[data-expanded="true"] & svg {
    transform: rotate(180deg);
  }
`;

const PropertyFilter = ({
  counts = [],
  selectedPropertyType,
  onSuburbSelect,
  onReset,
}) => {
  const [selectedTown, setSelectedTown] = useState(null);

  const handleTownClick = (town) => {
    setSelectedTown((prevTown) => (prevTown === town ? null : town));
  };

  const handleSuburbClick = (suburb) => {
    onSuburbSelect(suburb);
  };

  return (
    <PropertyFilterContainer itemCount={counts.length}>
      <FilterHeader>
        <FilterTitle>
          <FiMapPin />
          Filter by Location
        </FilterTitle>
        {onReset && (
          <ResetButton onClick={onReset}>
            <MdClose />
            Reset
          </ResetButton>
        )}
      </FilterHeader>

      <FilterContent>
        {counts.length === 0 ? (
          <EmptyState>
            <FiMapPin />
            <div>No areas found.</div>
          </EmptyState>
        ) : (
          counts.map(({ town, suburbs }) => (
            <TownFilter key={town}>
              <TownHeader
                onClick={() => handleTownClick(town)}
                data-expanded={selectedTown === town}
              >
                <span>{town}</span>
                <ChevronIcon>
                  {selectedTown === town ? <FiChevronUp /> : <FiChevronDown />}
                </ChevronIcon>
              </TownHeader>
              {selectedTown === town && (
                <TownOptions>
                  {suburbs.map(({ suburb, propertyCount }) => (
                    <TownOption
                      key={suburb}
                      onClick={() => handleSuburbClick(suburb)}
                    >
                      <span>{suburb}</span>
                      <PropertyCount>{propertyCount}</PropertyCount>
                    </TownOption>
                  ))}
                </TownOptions>
              )}
            </TownFilter>
          ))
        )}
      </FilterContent>
    </PropertyFilterContainer>
  );
};

export default PropertyFilter;
